import React from 'react';
import { Check, X, TrendingUp } from 'lucide-react';

const ComparisonTable = ({ industry = 'plumber' }) => {
  
  // Industry-specific comparison data
  const comparisonData = {
    plumber: {
      doItYourself: {
        name: "Keep Doing It Yourself",
        cost: "£0/month",
        realCost: "£2,824/month in lost revenue + wasted time",
        pros: ["No upfront cost"],
        cons: [
          "Still missing 8+ calls per week",
          "Spending 12+ hours/week on admin",
          "Can't be in two places at once",
          "Missing emergency calls = lost to competitors",
          "Quotes take hours or days to send",
          "Follow-ups? What follow-ups?",
          "Calendar chaos and double bookings"
        ]
      },
      hireStaff: {
        name: "Hire Office Staff",
        cost: "£2,400+/month",
        realCost: "£2,400/month salary + benefits",
        pros: ["Someone answers the phone"],
        cons: [
          "£2,400+ monthly salary",
          "National Insurance contributions",
          "Holiday pay & sick days",
          "Takes months to train",
          "Only works 9-5 (no evenings/weekends)",
          "Can't handle multiple calls at once",
          "Turnover risk - restart every 18 months"
        ]
      },
      competitors: {
        name: "Generic CRM Tools",
        cost: "£200-500/month",
        realCost: "£200-500/month + £50-100/hr consultant fees + your time",
        pros: ["Cheaper initially"],
        cons: [
          "Weeks to set up properly",
          "Not built for plumbers",
          "No AI answering service",
          "You're the tech support",
          "Still manually following up",
          "Clunky and complicated",
          "Per-user licensing fees add up fast",
          "Expensive add-ons for basic features"
        ]
      },
      lbh: {
        name: "Local Business Heroes",
        cost: "£497/month",
        realCost: "£497/month (makes you an extra £3,227/month net profit)",
        pros: [
          "AI optionally answers EVERY call 24/7/365 when you can't",
          "Or send automatic preset text replies instantly",
          "Instant quotes sent automatically",
          "Never miss an emergency call",
          "7-stage follow-up on every lead",
          "Automated reviews (67% respond)",
          "Up to 50 users included",
          "£50/month in credits included (SMS, calls, AI, emails)",
          "Smart calendar booking",
          "Missed call text-back in 60 seconds",
          "Setup done FOR you",
          "Dedicated success manager",
          "Built specifically for plumbers",
          "Priority support"
        ]
      }
    }
  };

  const data = comparisonData[industry] || comparisonData.plumber;

  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 border-2 border-slate-700">
      
      <div className="text-center mb-8">
        <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
          Let's Be Honest About Your Options
        </h3>
        <p className="text-xl text-gray-300">
          You have 4 choices. Only one makes business sense.
        </p>
      </div>

      <div className="space-y-6">
        
        {/* Option 1 */}
        <div className="bg-red-900/20 border-2 border-red-500/50 rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-2xl font-bold text-white mb-1">{data.doItYourself.name}</h4>
              <p className="text-3xl font-black text-red-400">{data.doItYourself.cost}</p>
              <p className="text-sm text-red-300 mt-1">Real cost: {data.doItYourself.realCost}</p>
            </div>
          </div>

          <div className="space-y-3">
            {data.doItYourself.pros.map((pro, i) => (
              <div key={i} className="flex items-start">
                <Check className="text-green-400 mr-2 mt-1 flex-shrink-0" size={16} />
                <span className="text-gray-300 text-sm">{pro}</span>
              </div>
            ))}
            {data.doItYourself.cons.map((con, i) => (
              <div key={i} className="flex items-start">
                <X className="text-red-400 mr-2 mt-1 flex-shrink-0" size={16} />
                <span className="text-gray-300 text-sm">{con}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Option 2 */}
        <div className="bg-orange-900/20 border-2 border-orange-500/50 rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-2xl font-bold text-white mb-1">{data.hireStaff.name}</h4>
              <p className="text-3xl font-black text-orange-400">{data.hireStaff.cost}</p>
              <p className="text-sm text-orange-300 mt-1">Real cost: {data.hireStaff.realCost}</p>
            </div>
          </div>

          <div className="space-y-3">
            {data.hireStaff.pros.map((pro, i) => (
              <div key={i} className="flex items-start">
                <Check className="text-green-400 mr-2 mt-1 flex-shrink-0" size={16} />
                <span className="text-gray-300 text-sm">{pro}</span>
              </div>
            ))}
            {data.hireStaff.cons.map((con, i) => (
              <div key={i} className="flex items-start">
                <X className="text-orange-400 mr-2 mt-1 flex-shrink-0" size={16} />
                <span className="text-gray-300 text-sm">{con}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Option 3 */}
        <div className="bg-yellow-900/20 border-2 border-yellow-500/50 rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-2xl font-bold text-white mb-1">{data.competitors.name}</h4>
              <p className="text-3xl font-black text-yellow-400">{data.competitors.cost}</p>
              <p className="text-sm text-yellow-300 mt-1">Real cost: {data.competitors.realCost}</p>
            </div>
          </div>

          <div className="space-y-3">
            {data.competitors.pros.map((pro, i) => (
              <div key={i} className="flex items-start">
                <Check className="text-green-400 mr-2 mt-1 flex-shrink-0" size={16} />
                <span className="text-gray-300 text-sm">{pro}</span>
              </div>
            ))}
            {data.competitors.cons.map((con, i) => (
              <div key={i} className="flex items-start">
                <X className="text-yellow-400 mr-2 mt-1 flex-shrink-0" size={16} />
                <span className="text-gray-300 text-sm">{con}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Option 4 - Winner */}
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/30 border-4 border-green-500 rounded-xl p-6 relative">
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-2 rounded-full font-black text-sm animate-pulse">
            ⭐ OBVIOUS WINNER ⭐
          </div>

          <div className="flex items-start justify-between mb-4 mt-4">
            <div>
              <h4 className="text-2xl font-bold text-white mb-1">{data.lbh.name}</h4>
              <p className="text-3xl font-black text-green-400">{data.lbh.cost}</p>
              <p className="text-sm text-green-300 mt-1">{data.lbh.realCost}</p>
            </div>
            <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center">
              <TrendingUp size={16} className="mr-1" />
              PROFIT
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-2">
            {data.lbh.pros.map((pro, i) => (
              <div key={i} className="flex items-start">
                <Check className="text-green-400 mr-2 mt-1 flex-shrink-0" size={16} />
                <span className="text-white text-sm font-medium">{pro}</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Bottom Comparison Summary */}
      <div className="mt-8 bg-slate-800/50 rounded-xl p-6 border border-slate-600">
        <h4 className="text-2xl font-bold text-white mb-4 text-center">Quick Math:</h4>
        
        <div className="grid md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-gray-400 text-sm mb-1">Do It Yourself</p>
            <p className="text-2xl font-black text-red-400">-£2,824/mo</p>
            <p className="text-xs text-gray-400">(lost revenue + time)</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm mb-1">Hire Staff</p>
            <p className="text-2xl font-black text-orange-400">-£2,400/mo</p>
            <p className="text-xs text-gray-400">(+ NI + benefits)</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm mb-1">Generic Tools</p>
            <p className="text-2xl font-black text-yellow-400">-£350/mo</p>
            <p className="text-xs text-gray-400">(avg + your time)</p>
          </div>
          <div className="bg-green-600/20 rounded-lg p-3">
            <p className="text-gray-300 text-sm mb-1 font-bold">LBH System</p>
            <p className="text-3xl font-black text-green-400">+£3,227/mo</p>
            <p className="text-xs text-green-300">NET PROFIT</p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-gray-300">
            <span className="text-white font-bold">You're either losing money</span> with the other options...<br />
            <span className="text-green-400 font-bold text-xl">Or making an extra £3,227/month</span> with our system.
          </p>
        </div>
      </div>

      <div className="mt-6 text-center">
        <p className="text-2xl text-white font-bold">
          Which one makes sense for YOUR business?
        </p>
      </div>

    </div>
  );
};

export default ComparisonTable;
